import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertDialog, Badge, Box, Button, Card, Checkbox, Dialog, DropdownMenu, Flex, Progress, RadioGroup, SegmentedControl,
    Select, Separator, Table, Text, TextField, Tooltip } from "@radix-ui/themes";
import { ChatBubbleIcon, PersonIcon, EnvelopeClosedIcon, MobileIcon, PlusIcon, InfoCircledIcon,
    EnvelopeOpenIcon, BookmarkIcon, ChevronLeftIcon, ChevronRightIcon, Pencil1Icon, TrashIcon, PauseIcon, PlayIcon, StopIcon,
    ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { useProfileContext } from "context";
import { useCampaignAction, useCampaignAudienceEstimate, useCampaigns, useCreateCampaign, useDeleteCampaign, useGetWhatsappCredentialAccounts, useUpdateCampaign, useMarketingEmailTemplates, useMarketingSenderDomains, useSegments, useContactTags } from "hooks";
import { ToastAlert } from "components";
import { useToast } from "../../context/ToastContext";
import { DateTimePicker } from "components/datepicker/DateTimePicker";
import { sendTestEmail } from "../../api/bMarketingApi";
import MarketingWhatsappCampaigns, { MarketingWhatsappCampaignDialog } from "./MarketingWhatsappCampaigns";
import ElegantClassicTemplate from './templates/ElegantClassicEmailTemplate';
import ThankYouReviewTemplate from './templates/ThankYouReviewTemplate';
import HospitalityPromoTemplate from './templates/HospitalityPromoTemplate';

const CHANNEL_OPTIONS = [
  { value: "all", label: "All" },
  { value: "email", label: "Email", icon: EnvelopeClosedIcon },
  { value: "sms", label: "SMS", icon: MobileIcon },
  { value: "whatsapp", label: "WhatsApp", icon: ChatBubbleIcon },
];

const SUB_TYPE_OPTIONS = [
  { value: "welcome", label: "Welcome" },
  { value: "newsletter", label: "Newsletter" },
  { value: "review", label: "Review" },
  { value: "promotion", label: "Promotion / Offers" },
];

const TEMPLATES = {
  'elegant-classic': ElegantClassicTemplate,
  'thank-you-review': ThankYouReviewTemplate,
  'hospitality-promo': HospitalityPromoTemplate,
};

const PURPOSE_TEMPLATE_MAP = {
  welcome: 'elegant-classic',
  review: 'thank-you-review',
  promotion: 'hospitality-promo',
  newsletter: 'elegant-classic',
};

const STATUS_COLORS = {
  draft: "gray",
  scheduled: "amber",
  queued: "blue",
  processing: "blue",
  pausing: "amber",
  sent: "green",
  failed: "red",
  paused: "orange",
  paused_limit: "red",
  cancelled: "gray",
};

const EMAIL_EXCLUSION_LABELS = {
  missing_email: "Missing email",
  unsubscribed: "Unsubscribed",
  invalid_email: "Invalid email",
  risky_not_included: "Risky",
  unknown_not_included: "Inconclusive",
  unknown_retry_pending: "Inconclusive — retry pending",
  validation_pending: "Pending validation",
  validation_processing: "Checking",
  validation_unavailable: "Validation unavailable",
  hard_bounce: "Hard bounced",
  dropped: "Permanently dropped",
  spam_report: "Spam reported",
};

const STEP_TITLES = [
  "Campaign",
  "Audience",
  "Template",
  "Schedule",
  "Review",
];

const DEFAULT_CAMPAIGN_FORM = {
  name: "",
  campaign_type: "email",
  sub_type: "welcome",
  audience_type: "all_contacts",
  segments: [],
  tags: [],
  template: "",
  sender_domain: "",
  send_mode: "now",
  scheduled_send_at: "",
  include_risky_emails: false,
  include_unknown_emails: false,
};

const WHATSAPP_CAMPAIGN_INTEGRATION_REQUIRED_MESSAGE = "Connect a WhatsApp integration before creating WhatsApp campaigns.";

function formatDateTime(value) {
  if (!value) {
    return "Send now";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Invalid date";
  }

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatCampaignDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function extractErrorMessage(error) {
  const data = error?.response?.data;

  if (!data) {
    return "Failed to save campaign";
  }

  if (typeof data.error === "string") {
    return data.error;
  }

  if (typeof data.detail === "string") {
    return data.detail;
  }

  const firstValue = Object.values(data)[0];
  if (typeof firstValue === "string") {
    return firstValue;
  }
  if (Array.isArray(firstValue) && firstValue[0]) {
    return firstValue[0];
  }

  return "Failed to save campaign";
}

const TruncatedText = ({ children, size = "2", weight = "regular", color = undefined }) => {
  const text = typeof children === "string" ? children : String(children ?? "");
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const check = () => {
      const el = containerRef.current;
      if (el) {
        setIsTruncated(el.scrollWidth > el.clientWidth);
      }
    };

    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [text]);

  const textNode = (
    <Box
      as="div"
      ref={containerRef}
      style={{ width: "100%", minWidth: 0, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
    >
      <Text size={size} weight={weight} color={color}>{text}</Text>
    </Box>
  );

  return isTruncated ? <Tooltip content={text}>{textNode}</Tooltip> : textNode;
};

function StepBadge({ index, currentStep }) {
  const isActive = index + 1 === currentStep;
  const isComplete = index + 1 < currentStep;

  return (
    <Badge variant={isActive ? "solid" : "soft"} color={isComplete ? "green" : isActive ? "blue" : "gray"} size="2">
      {index + 1}. {STEP_TITLES[index]}
    </Badge>
  );
}

function renderCampaignChannelLabel(campaign) {
  const channelLabel = String(campaign?.campaign_type || "").toUpperCase();
  const subTypeLabel = String(campaign?.sub_type || "").trim();

  return subTypeLabel ? `${channelLabel} • ${subTypeLabel}` : channelLabel;
}

function renderCampaignSender(campaign) {
  if (campaign?.campaign_type === "whatsapp") {
    return campaign?.whatsapp_sender_phone_number || campaign?.whatsapp_phone_number_id || "—";
  }

  return campaign?.sender_domain_details?.default_sender_email || "noreply@b-connect.co.uk";
}

const getTemplatePreviewHtml = (template) => {
  if (!template) return "";

  const isImported = template.template_source && template.template_source !== 'internal';

  if (template.template_type === 'scratch') {
    if (template.html_content) return template.html_content;

    const orgName = template.name || 'Your Brand';
    return `<div style="background:#F3F4F6;padding:24px 20px;font-family:Arial,sans-serif;">
      <div style="max-width:600px;margin:0 auto;background:#FFFFFF;border-radius:10px;overflow:hidden;box-shadow:0 1px 8px rgba(0,0,0,0.07);">
        <div style="padding:80px 30px;text-align:center;">
          <div style="font-size:16px;font-weight:700;margin-bottom:6px;color:#111827;">Start building your email</div>
          <div style="font-size:14px;color:#9CA3AF;">Drag blocks from the left panel, or click them to add.</div>
        </div>
        <div style="border-top:1px solid #e5e7eb;background:#f9fafb;padding:28px 30px 24px;text-align:center;">
          <div style="font-size:13px;color:#9CA3AF;">&copy; ${new Date().getFullYear()} ${orgName}. All rights reserved.</div>
        </div>
      </div>
    </div>`;
  }

  if (isImported && template.html_content) {
    return template.html_content;
  }

  const designJson = template.design_json || {};
  const templateId = designJson.templateId || PURPOSE_TEMPLATE_MAP[template.purpose] || 'elegant-classic';
  const templateModule = TEMPLATES[templateId];

  if (!templateModule) return "";

  const formData = {
    ...templateModule.getDefaultValues(template.purpose),
    ...designJson,
  };

  return templateModule.generatePreviewHtml(formData);
};

const TemplatePreviewPanel = ({ template }) => {
  const previewHtml = useMemo(() => getTemplatePreviewHtml(template), [template]);

  return (
    <Flex direction="column" gap="2" style={{ flex: '0 0 420px', minWidth: 320, minHeight: 0, overflow: 'hidden' }}>
      <Text weight="medium" size="2" align="center" justify="center" color="gray">
        Template Preview
      </Text>
      <Box style={{
        flex: '1 1 0',
        minHeight: 0,
        border: '1px solid var(--gray-a6)',
        borderRadius: 'var(--radius-3)',
        background: 'var(--gray-2)',
        overflow: 'hidden'
      }}>
        {previewHtml ? (
          <iframe
            title={template?.name ? `${template.name} preview` : 'Template preview'}
            srcDoc={previewHtml}
            sandbox=""
            style={{ width: '100%', height: '100%', border: 0, background: '#FFFFFF' }}
          />
        ) : (
          <Flex direction="column" align="center" justify="center" gap="2" height="100%" p="5" style={{ textAlign: 'center' }}>
            <Text weight="medium" size="2">No template selected</Text>
            <Text size="2" color="gray">Choose an email template to preview it here.</Text>
          </Flex>
        )}
      </Box>
    </Flex>
  );
};

const dialogBodyStyle = {
  flex: '1 1 auto',
  minHeight: 0,
  overflow: 'hidden',
  alignItems: 'stretch'
};

const dialogFormPaneStyle = {
  flex: '1 1 0',
  minWidth: 360,
  minHeight: 0,
  overflowY: 'auto',
  paddingRight: 'var(--space-2)'
};

const dialogStepContentStyle = {
  width: 'min(100%, 540px)',
};

const dialogSeparatorStyle = {
  alignSelf: 'stretch',
  minHeight: '100%'
};

function campaignToForm(campaign) {
  return {
    ...DEFAULT_CAMPAIGN_FORM,
    name: campaign.name || "",
    campaign_type: campaign.campaign_type || "email",
    sub_type: campaign.sub_type || "welcome",
    audience_type: campaign.audience_type || "all_contacts",
    segments: (campaign.segments || []).map(String),
    tags: (campaign.tags || []).map(String),
    template: campaign.template ? String(campaign.template) : "",
    sender_domain: campaign.sender_domain ? String(campaign.sender_domain) : "default",
    send_mode: campaign.send_mode || "now",
    scheduled_send_at: campaign.scheduled_send_at || "",
    include_risky_emails: Boolean(campaign.include_risky_emails),
    include_unknown_emails: Boolean(campaign.include_unknown_emails),
  };
}

function canEditCampaign(campaign) {
  return canDeleteCampaign(campaign);
}

function canDeleteCampaign(campaign) {
  return (
    campaign?.status === "scheduled" &&
    campaign?.send_mode === "scheduled" &&
    new Date(campaign?.scheduled_send_at).getTime() > Date.now()
  );
}

function canPauseCampaign(campaign) {
  return campaign?.status === "processing";
}

function canResumeCampaign(campaign) {
  return campaign?.status === "paused" || campaign?.status === "paused_limit";
}

function canCancelCampaign(campaign) {
  return ["processing", "pausing", "paused", "paused_limit"].includes(campaign?.status);
}

function getProcessedCount(campaign) {
  return Number(campaign?.sent_count || 0) + Number(campaign?.failed_count || 0) + Number(campaign?.skipped_count || 0);
}

function getDeliveryProgress(campaign) {
  const total = Number(campaign?.recipient_count || 0);
  if (!total) {
    return 0;
  }
  return Math.min(100, Math.round((getProcessedCount(campaign) / total) * 100));
}

export default function MarketingCampaigns() {
  const { userProfile } = useProfileContext();
  const { setToastMessage } = useToast();
  const organisationId = userProfile?.organisation_id;

  const [selectedChannel, setSelectedChannel] = useState("all");
  const [campaignPage, setCampaignPage] = useState(1);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [campaignToCancel, setCampaignToCancel] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isWhatsappCreateDialogOpen, setIsWhatsappCreateDialogOpen] = useState(false);
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testEmailError, setTestEmailError] = useState("");
  const [testSenderDomainId, setTestSenderDomainId] = useState("default");
  const [campaignForm, setCampaignForm] = useState(DEFAULT_CAMPAIGN_FORM);

  const shouldLoadEmailAudienceData = isCreateDialogOpen && currentStep >= 2;
  const campaignType = selectedChannel === "email" ? "email" : "";
  const { campaigns, pagination, isLoading } = useCampaigns(organisationId, {
    page: campaignPage,
    pageSize: 25,
    campaignType,
    enabled: selectedChannel !== "whatsapp" && selectedChannel !== "sms",
  });
  const { segments } = useSegments(organisationId, { enabled: shouldLoadEmailAudienceData });
  const { contactTags } = useContactTags(organisationId, { enabled: shouldLoadEmailAudienceData });
  const { emailTemplates } = useMarketingEmailTemplates(organisationId);
  const { senderDomains } = useMarketingSenderDomains(organisationId);
  const { data: whatsappAccountsData } = useGetWhatsappCredentialAccounts(organisationId);
  const whatsappAccounts = useMemo(() => whatsappAccountsData?.data?.accounts || [], [whatsappAccountsData]);
  const hasWhatsappIntegration = whatsappAccounts.length > 0;
  const createCampaignMutation = useCreateCampaign();
  const updateCampaignMutation = useUpdateCampaign();
  const deleteCampaignMutation = useDeleteCampaign();
  const campaignActionMutation = useCampaignAction();

  const isEditingCampaign = Boolean(editingCampaign);

  const {
    recipientCount: estimatedRecipients,
    selectedCount: selectedAudienceCount,
    excludedCount: excludedAudienceCount,
    exclusions: audienceExclusions,
    validationCounts: audienceValidationCounts,
    isLoading: isEstimateLoading,
  } = useCampaignAudienceEstimate(
    organisationId,
    campaignForm.audience_type,
    campaignForm.segments,
    "email",
    campaignForm.tags,
    {
      enabled: shouldLoadEmailAudienceData,
      includeRisky: campaignForm.include_risky_emails,
      includeUnknown: campaignForm.include_unknown_emails,
    },
  );

  const filteredCampaigns = useMemo(() => {
    if (selectedChannel === "all") {
      return campaigns;
    }
    return campaigns.filter((campaign) => campaign.campaign_type === selectedChannel);
  }, [campaigns, selectedChannel]);

  const handleSelectedChannelChange = (value) => {
    setSelectedChannel(value);
    setCampaignPage(1);
  };

  const filteredTemplates = useMemo(() => {
    return (emailTemplates || []).filter((template) => template.purpose === campaignForm.sub_type);
  }, [emailTemplates, campaignForm.sub_type]);

  const activeSenderDomains = useMemo(() => {
    return (senderDomains || []).filter((domain) => (
      domain.status === "verified" && domain.link_status === "verified" && domain.is_active
    ));
  }, [senderDomains]);

  const selectedSegments = useMemo(() => {
    const selectedIds = new Set(campaignForm.segments.map(String));
    return (segments || []).filter((segment) => selectedIds.has(String(segment.id)));
  }, [segments, campaignForm.segments]);

  const selectedTags = useMemo(() => {
    const selectedIds = new Set(campaignForm.tags.map(String));
    return (contactTags || []).filter((tag) => selectedIds.has(String(tag.id)));
  }, [contactTags, campaignForm.tags]);

  const selectedTemplate = useMemo(() => {
    return (emailTemplates || []).find((template) => String(template.id) === String(campaignForm.template));
  }, [emailTemplates, campaignForm.template]);

  const selectedCampaignSenderDomain = useMemo(() => {
    return activeSenderDomains.find((domain) => String(domain.id) === String(campaignForm.sender_domain));
  }, [activeSenderDomains, campaignForm.sender_domain]);

  const selectedSegmentNames = useMemo(() => {
    return selectedSegments.map((segment) => segment.name);
  }, [selectedSegments]);

  const selectedTagNames = useMemo(() => {
    return selectedTags.map((tag) => tag.name);
  }, [selectedTags]);

  const resetDialog = () => {
    setCurrentStep(1);
    setEditingCampaign(null);
    setCampaignForm(DEFAULT_CAMPAIGN_FORM);
  };

  useEffect(() => {
    if (!isCreateDialogOpen) {
      resetDialog();
    }
  }, [isCreateDialogOpen]);

  const openCreateDialog = () => {
    setEditingCampaign(null);
    setCampaignForm(DEFAULT_CAMPAIGN_FORM);
    setCurrentStep(1);
    setIsCreateDialogOpen(true);
  };

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignForm(campaignToForm(campaign));
    setCurrentStep(1);
    setIsCreateDialogOpen(true);
  };

  const stepIsValid = useMemo(() => {
    if (currentStep === 1) {
      return campaignForm.name.trim().length > 0 && campaignForm.campaign_type === "email";
    }

    if (currentStep === 2) {
      if (campaignForm.audience_type === "all_contacts") {
        return estimatedRecipients > 0;
      }
      if (campaignForm.audience_type === "tag") {
        return campaignForm.tags.length > 0;
      }
      return campaignForm.segments.length > 0;
    }

    if (currentStep === 3) {
      return !!campaignForm.sub_type && !!campaignForm.template;
    }

    if (currentStep === 4) {
      if (!campaignForm.sender_domain) {
        return false;
      }

      if (campaignForm.send_mode === "now") {
        return true;
      }
      if (!campaignForm.scheduled_send_at) {
        return false;
      }
      return new Date(campaignForm.scheduled_send_at).getTime() > Date.now();
    }

    return true;
  }, [campaignForm, currentStep, estimatedRecipients]);

  const handleNext = () => {
    if (!stepIsValid) {
      if (currentStep === 2 && campaignForm.audience_type === "all_contacts") {
        setToastMessage("No eligible contacts are available for this campaign");
      } else if (currentStep === 2 && campaignForm.audience_type === "tag") {
        setToastMessage("Select at least one tag");
      } else if (currentStep === 4 && !campaignForm.sender_domain) {
        setToastMessage("Choose the sender email for this campaign");
      } else if (currentStep === 4 && campaignForm.send_mode === "scheduled") {
        setToastMessage("Choose a future date and time");
      }
      return;
    }

    setCurrentStep((step) => Math.min(step + 1, 5));
  };

  const handleConfirm = async () => {
    const payload = {
      organisation: organisationId,
      name: campaignForm.name.trim(),
      campaign_type: campaignForm.campaign_type,
      sub_type: campaignForm.sub_type,
      audience_type: campaignForm.audience_type,
      segments: campaignForm.audience_type === "segment" ? campaignForm.segments.map(Number) : [],
      tags: campaignForm.audience_type === "tag" ? campaignForm.tags.map(Number) : [],
      template: Number(campaignForm.template),
      sender_domain: campaignForm.sender_domain === "default" ? null : Number(campaignForm.sender_domain),
      send_mode: campaignForm.send_mode,
      scheduled_send_at: campaignForm.send_mode === "scheduled"
        ? new Date(campaignForm.scheduled_send_at).toISOString()
        : null,
      include_risky_emails: campaignForm.include_risky_emails,
      include_unknown_emails: campaignForm.include_unknown_emails,
    };

    try {
      if (isEditingCampaign) {
        await updateCampaignMutation.mutateAsync({
          campaignId: editingCampaign.campaign_id,
          data: payload,
        });
        setToastMessage("Scheduled campaign updated");
      } else {
        await createCampaignMutation.mutateAsync(payload);
        setToastMessage(campaignForm.send_mode === "scheduled" ? "Campaign scheduled" : "Campaign queued for sending");
      }
      setIsCreateDialogOpen(false);
    } catch (error) {
      setToastMessage(extractErrorMessage(error));
    }
  };

  const getConfirmButtonLabel = () => {
    if (isEditingCampaign) {
      return "Save changes";
    }
    return campaignForm.send_mode === "scheduled" ? "Confirm and schedule" : "Confirm and send";
  };

  const handleDeleteCampaign = async () => {
    if (!campaignToDelete) {
      return;
    }

    try {
      await deleteCampaignMutation.mutateAsync(campaignToDelete.campaign_id);
      setToastMessage("Scheduled campaign deleted");
      setCampaignToDelete(null);
    } catch (error) {
      setToastMessage(extractErrorMessage(error));
    }
  };

  const handleCampaignAction = async (campaign, action) => {
    if (!campaign) {
      return;
    }

    try {
      await campaignActionMutation.mutateAsync({
        campaignId: campaign.campaign_id,
        action,
      });
      const actionLabels = {
        pause: "Campaign pause requested",
        resume: "Campaign resumed",
        cancel: "Campaign cancelled",
      };
      setToastMessage(actionLabels[action] || "Campaign updated");
      if (action === "cancel") {
        setCampaignToCancel(null);
      }
    } catch (error) {
      setToastMessage(extractErrorMessage(error));
    }
  };

  const renderAudienceSummary = (campaign) => {
    if (campaign.audience_type === "all_contacts") {
      return "All eligible contacts";
    }
    if (campaign.audience_type === "tag") {
      return campaign.tag_names?.length ? campaign.tag_names.join(", ") : "Tags";
    }
    return campaign.segment_names?.length ? campaign.segment_names.join(", ") : "Segments";
  };

  const renderScheduleSummary = (campaign) => {
    if (campaign.send_mode === "scheduled") {
      return formatDateTime(campaign.scheduled_send_at);
    }
    return campaign.sent_at ? `Sent ${formatDateTime(campaign.sent_at)}` : "Send now";
  };

  const handleSendTestEmail = async () => {
    setTestEmailError("");

    if (!testEmailAddress.trim()) {
      setTestEmailError("Please enter an email address");
      return;
    }

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(testEmailAddress)) {
      setTestEmailError("Please enter a valid email address");
      return;
    }

    if (!campaignForm.template) {
      setTestEmailError("No template selected");
      return;
    }

    setIsSendingTest(true);

    try {
      const senderDomainId = testSenderDomainId === "default" ? null : testSenderDomainId;
      await sendTestEmail(selectedTemplate.template_id, testEmailAddress, senderDomainId);
      setToastMessage(`Test email sent successfully to ${testEmailAddress}`);
      setTestEmailDialogOpen(false);
      setTestEmailAddress("");
      setTestEmailError("");
      setTestSenderDomainId("default");
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || "Failed to send test email";
      setTestEmailError(errorMessage);
    } finally {
      setIsSendingTest(false);
    }
  };

  const toggleSegment = (segmentId, checked) => {
    setCampaignForm((prev) => {
      const nextSegments = checked
        ? [...prev.segments, String(segmentId)]
        : prev.segments.filter((value) => value !== String(segmentId));

      return {
        ...prev,
        segments: Array.from(new Set(nextSegments)),
      };
    });
  };

  const toggleTag = (tagId, checked) => {
    setCampaignForm((prev) => {
      const nextTags = checked
        ? [...prev.tags, String(tagId)]
        : prev.tags.filter((value) => value !== String(tagId));

      return {
        ...prev,
        tags: Array.from(new Set(nextTags)),
      };
    });
  };

  if (selectedChannel === "whatsapp") {
    return (
      <>
        <MarketingWhatsappCampaigns
          selectedChannel={selectedChannel}
          onSelectedChannelChange={handleSelectedChannelChange}
        />
        <ToastAlert />
      </>
    );
  }

  return (
    <>
      {(isLoading || isEstimateLoading || createCampaignMutation.isPending || updateCampaignMutation.isPending || deleteCampaignMutation.isPending || campaignActionMutation.isPending) && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000 }}>
          <Progress size="1" duration="1" />
        </div>
      )}

      <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "center", flex: "1 0 0" }}>
        <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
          <SegmentedControl.Root value={selectedChannel} onValueChange={handleSelectedChannelChange}>
            {CHANNEL_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <SegmentedControl.Item key={option.value} value={option.value}>
                  {Icon ? (
                    <Flex gap="1" align="center">
                      <Icon />
                      <Text>{option.label}</Text>
                    </Flex>
                  ) : option.label}
                </SegmentedControl.Item>
              );
            })}
          </SegmentedControl.Root>

          <Flex gap="2" align="center">
            {selectedChannel === "all" ? (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button variant="soft">
                    <PlusIcon width="16" height="16" />
                    Create campaign
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  <DropdownMenu.Item onClick={openCreateDialog}>
                    <Flex gap="2" align="center">
                      <EnvelopeClosedIcon />
                      <Text>Email campaign</Text>
                    </Flex>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onSelect={(event) => {
                      if (!hasWhatsappIntegration) {
                        event.preventDefault();
                        return;
                      }

                      setIsWhatsappCreateDialogOpen(true);
                    }}
                    style={!hasWhatsappIntegration ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                  >
                    <Tooltip content={hasWhatsappIntegration ? "Create WhatsApp campaign" : WHATSAPP_CAMPAIGN_INTEGRATION_REQUIRED_MESSAGE}>
                    <Flex gap="2" align="center">
                      <ChatBubbleIcon />
                      <Text>WhatsApp campaign</Text>
                    </Flex>
                    </Tooltip>
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            ) : (
              <Button
                variant="soft"
                onClick={openCreateDialog}
                disabled={selectedChannel === "sms"}
              >
                <PlusIcon width="16" height="16" />
                Create campaign
              </Button>
            )}
          </Flex>
        </Flex>

        {isWhatsappCreateDialogOpen ? (
          <MarketingWhatsappCampaignDialog
            open={isWhatsappCreateDialogOpen}
            onOpenChange={setIsWhatsappCreateDialogOpen}
          />
        ) : null}

        {selectedChannel === "sms" ? (
          <Flex
            direction="column"
            style={{
              justifyContent: "center",
              alignItems: "center",
              alignSelf: "stretch",
              border: "1px dashed var(--gray-a6)",
              borderRadius: "var(--radius-4)",
              height: "100%",
              flex: "1 0 0",
            }}
          >
            <Text color="gray">SMS features coming soon.</Text>
          </Flex>
        ) : filteredCampaigns.length === 0 ? (
          <Flex
            direction="column"
            style={{
              justifyContent: "center",
              alignItems: "center",
              alignSelf: "stretch",
              border: "1px dashed var(--gray-a6)",
              borderRadius: "var(--radius-4)",
              height: "100%",
              flex: "1 0 0",
              padding: "var(--space-6)",
            }}
          >
            <Text color="gray">No campaigns yet. Create your first campaign to send to your audience.</Text>
          </Flex>
        ) : (
          <>
          <Card style={{ display: "flex", alignSelf: "stretch", flexDirection: "column", alignItems: "flex-start",
            flex: "1 0 0", width: "100%", minWidth: 0, height: "100%", maxWidth: "100%", overflowY: "auto", overflowX: "hidden" }}>
            <Table.Root layout="fixed" style={{ width: "100%", minWidth: 0, height: "100%" }}>
                <Table.Header style={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "var(--color-panel-solid)" }}>
                    <Table.Row align="center">
                        <Table.ColumnHeaderCell style={{ width: "12%", minWidth: 0 }}><TruncatedText weight="medium">Campaign</TruncatedText></Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell style={{ width: "11%", minWidth: 0 }}><TruncatedText weight="medium">Audience</TruncatedText></Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell style={{ width: "11%", minWidth: 0 }}><TruncatedText weight="medium">Template</TruncatedText></Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell style={{ width: "11%", minWidth: 0 }}><TruncatedText weight="medium">Schedule</TruncatedText></Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell style={{ width: "11%", minWidth: 0 }}><TruncatedText weight="medium">Sender</TruncatedText></Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell style={{ width: "10%", minWidth: 0 }}><TruncatedText weight="medium">Delivery</TruncatedText></Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell style={{ width: "9%", minWidth: 0 }}><TruncatedText weight="medium">Status</TruncatedText></Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell style={{ width: "9%", minWidth: 0 }}><TruncatedText weight="medium">Created on</TruncatedText></Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell style={{ width: "9%", minWidth: 0 }}><TruncatedText weight="medium">Updated on</TruncatedText></Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell style={{ width: "7%", minWidth: 0 }}><TruncatedText weight="medium">Actions</TruncatedText></Table.ColumnHeaderCell>
                    </Table.Row>
                </Table.Header>
            <Table.Body>
                {filteredCampaigns.map((campaign) => (
                <Table.Row key={campaign.id}>
                    <Table.Cell style={{ minWidth: 0 }}>
                    <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
                        <TruncatedText weight="medium">{campaign.name}</TruncatedText>
                    <TruncatedText size="1" color="gray">{renderCampaignChannelLabel(campaign)}</TruncatedText>
                    </Flex>
                    </Table.Cell>
                    <Table.Cell style={{ minWidth: 0 }}>
                    <TruncatedText>{renderAudienceSummary(campaign)}</TruncatedText>
                    </Table.Cell>
                    <Table.Cell style={{ minWidth: 0 }}>
                    <TruncatedText>{campaign.template_name || "No template"}</TruncatedText>
                    </Table.Cell>
                    <Table.Cell style={{ minWidth: 0 }}>
                    <TruncatedText>{renderScheduleSummary(campaign)}</TruncatedText>
                    </Table.Cell>
                    <Table.Cell style={{ minWidth: 0 }}>
                    <TruncatedText>{renderCampaignSender(campaign)}</TruncatedText>
                    </Table.Cell>
                    <Table.Cell>
                    <Flex direction="column" align="start" gap="1" style={{ minWidth: 0, lineHeight: 1.2 }}>
                      <TruncatedText>{`${getProcessedCount(campaign)} / ${campaign.recipient_count}`}</TruncatedText>
                      <Progress value={getDeliveryProgress(campaign)} size="1" style={{ width: "100%" }} />
                      {Number(campaign.sent_count || 0) > 0 && (
                        <TruncatedText size="1" color="gray">{`${campaign.sent_count} sent`}</TruncatedText>
                      )}
                      {campaign.failed_count > 0 && (
                      <TruncatedText size="1" color="red">{`${campaign.failed_count} failed`}</TruncatedText>
                      )}
                      {campaign.skipped_count > 0 && (
                      <TruncatedText size="1" color="gray">{`${campaign.skipped_count} skipped`}</TruncatedText>
                      )}
                    </Flex>
                    </Table.Cell>
                    <Table.Cell>
                    <Badge color={STATUS_COLORS[campaign.status] || "gray"} variant="soft" style={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {campaign.status === "paused_limit" ? "paused · usage limit" : campaign.status}
                    </Badge>
                    {campaign.status === "paused_limit" && campaign.last_error && (
                      <Tooltip content={campaign.last_error}>
                        <ExclamationTriangleIcon color="var(--red-9)" />
                      </Tooltip>
                    )}
                    </Table.Cell>
                    <Table.Cell style={{ minWidth: 0 }}>
                      <TruncatedText color="gray">{formatCampaignDateTime(campaign.created_at)}</TruncatedText>
                    </Table.Cell>
                    <Table.Cell style={{ minWidth: 0 }}>
                      <TruncatedText color="gray">{formatCampaignDateTime(campaign.updated_at)}</TruncatedText>
                    </Table.Cell>
                    <Table.Cell>
                    <Flex gap="1" wrap="wrap">
                      <Tooltip content={canEditCampaign(campaign) ? "Edit scheduled campaign" : "Only scheduled campaigns can be edited"}>
                        <Button
                          size="1"
                          variant="soft"
                          color="gray"
                          aria-label="Edit scheduled campaign"
                          disabled={!canEditCampaign(campaign)}
                          onClick={() => openEditDialog(campaign)}
                        >
                          <Pencil1Icon width="14" height="14" />
                        </Button>
                      </Tooltip>

                      <Tooltip content={canDeleteCampaign(campaign) ? "Delete scheduled campaign" : "Only scheduled campaigns can be deleted"}>
                        <Button
                          size="1"
                          variant="soft"
                          color="red"
                          aria-label="Delete scheduled campaign"
                          disabled={!canDeleteCampaign(campaign)}
                          onClick={() => setCampaignToDelete(campaign)}
                        >
                          <TrashIcon width="14" height="14" />
                        </Button>
                      </Tooltip>

                      {canPauseCampaign(campaign) && (
                        <Tooltip content="Pause campaign">
                          <Button
                            size="1"
                            variant="soft"
                            color="amber"
                            aria-label="Pause campaign"
                            onClick={() => handleCampaignAction(campaign, "pause")}
                          >
                            <PauseIcon width="14" height="14" />
                          </Button>
                        </Tooltip>
                      )}

                      {canResumeCampaign(campaign) && (
                        <Tooltip content="Resume campaign">
                          <Button
                            size="1"
                            variant="soft"
                            color="green"
                            aria-label="Resume campaign"
                            onClick={() => handleCampaignAction(campaign, "resume")}
                          >
                            <PlayIcon width="14" height="14" />
                          </Button>
                        </Tooltip>
                      )}

                      {canCancelCampaign(campaign) && (
                        <Tooltip content="Cancel remaining sends">
                          <Button
                            size="1"
                            variant="soft"
                            color="red"
                            aria-label="Cancel remaining sends"
                            onClick={() => setCampaignToCancel(campaign)}
                          >
                            <StopIcon width="14" height="14" />
                          </Button>
                        </Tooltip>
                      )}
                    </Flex>
                    </Table.Cell>
                </Table.Row>
                ))}
            </Table.Body>
            </Table.Root>
          </Card>
          {pagination.numPages > 1 && (
            <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
              <Text size="2" color="gray">
                Showing {(pagination.currentPage - 1) * 25 + 1} – {Math.min(pagination.currentPage * 25, pagination.count)} of {pagination.count} campaigns
              </Text>
              <Flex gap="2" align="center">
                <Button
                  variant="soft"
                  disabled={!pagination.hasPrevious}
                  onClick={() => setCampaignPage(pagination.currentPage - 1)}
                >
                  <ChevronLeftIcon />
                  Previous
                </Button>
                <Text size="2">Page {pagination.currentPage} of {pagination.numPages}</Text>
                <Button
                  variant="soft"
                  disabled={!pagination.hasNext}
                  onClick={() => setCampaignPage(pagination.currentPage + 1)}
                >
                  Next
                  <ChevronRightIcon />
                </Button>
              </Flex>
            </Flex>
          )}
          </>
        )}
      </Flex>

      <Dialog.Root open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <Dialog.Content style={{
          height: 'min(760px, calc(100vh - 96px))',
          maxHeight: 'calc(100vh - 96px)',
          width: 'min(1120px, calc(100vw - 32px))',
          maxWidth: 1120,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <Dialog.Title>{isEditingCampaign ? "Edit Campaign" : "Create Campaign"}</Dialog.Title>
          <Dialog.Description size="2" color="gray" mb="4">
            {isEditingCampaign
              ? "Update this scheduled campaign before it is sent."
              : "Build a campaign in five quick steps and send it or schedule it for later."}
          </Dialog.Description>

          <Flex gap="2" wrap="wrap">
            {STEP_TITLES.map((_, index) => (
              <StepBadge key={STEP_TITLES[index]} index={index} currentStep={currentStep} />
            ))}
          </Flex>

          <Flex gap="4" px="1" mt="4" style={dialogBodyStyle}>
            <Flex direction="column" gap="4" style={dialogFormPaneStyle}>
              <Flex direction="column" gap="4" style={dialogStepContentStyle}>

                {/* Campaign details step */}
                {currentStep === 1 && (
                  <>
                    <Flex direction="column" gap="1" style={{ alignSelf: "stretch" }}>
                      <Text size="2" weight="medium">Campaign name</Text>
                      <TextField.Root
                        placeholder="Spring welcome campaign"
                        value={campaignForm.name}
                        onChange={(event) => setCampaignForm((prev) => ({ ...prev, name: event.target.value }))}
                      />
                    </Flex>

                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">Campaign type</Text>
                      <Select.Root
                        value={campaignForm.campaign_type}
                        onValueChange={(value) => setCampaignForm((prev) => ({ ...prev, campaign_type: value }))}
                      >
                        <Select.Trigger style={{ width: "100%" }} />
                        <Select.Content>
                          <Select.Item value="email">Email campaign</Select.Item>
                          <Select.Item value="sms" disabled>SMS campaign (Coming soon)</Select.Item>
                          <Select.Item value="whatsapp" disabled>WhatsApp campaign (Coming soon)</Select.Item>
                        </Select.Content>
                      </Select.Root>
                    </Flex>
                  </>
                )}

                {/* Audience selection step */}
                {currentStep === 2 && (
                  <>
                    <Flex direction="column" gap="2">
                      <Text size="2" weight="medium">Audience source</Text>
                      <RadioGroup.Root
                        value={campaignForm.audience_type}
                        onValueChange={(value) => setCampaignForm((prev) => ({
                          ...prev,
                          audience_type: value,
                          segments: value === "segment" ? prev.segments : [],
                          tags: value === "tag" ? prev.tags : [],
                        }))}>
                        <Flex direction="column" gap="3">
                            {/* All contacts card  */}
                          <Card variant={campaignForm.audience_type === "all_contacts" ? "surface" : "classic"}>
                            <Text as="label" size="2" style={{ width: "100%" }}>
                                <Flex gap="1" align="center" justify="between" style={{ flex: 1 }}>
                                    <Flex align="center" gap="3">
                                        <RadioGroup.Item value="all_contacts" />All contacts
                                        <Tooltip content="All contacts are evaluated; only opted-in contacts with a deliverable, non-suppressed email address will be sent to.">
                                            <InfoCircledIcon />
                                        </Tooltip>
                                    </Flex>
                                    {/* <Badge>
                                        <PersonIcon width={12} height={12} />
                                        {estimatedRecipients.toLocaleString()} contacts
                                    </Badge> */}
                                </Flex>
                            </Text>
                          </Card>

                            {/* Segments card */}
                          <Card variant={campaignForm.audience_type === "segment" ? "surface" : "classic"}
                            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                            <Text as="label" size="2" style={{ width: "100%" }}>
                                <Flex align="center" gap="3">
                                    <RadioGroup.Item value="segment" />Segments
                                    <Tooltip content="Selected segment contacts are evaluated; only opted-in contacts with a deliverable, non-suppressed email address will be sent to.">
                                        <InfoCircledIcon />
                                    </Tooltip>
                                </Flex>
                            </Text>

                            <Flex direction="column" gap="3" style={{ flex: 1, display: campaignForm.audience_type === "segment" ? "flex" : "none" }}>
                                {campaignForm.audience_type === "segment" && (
                                <Flex direction="column" gap="2"
                                    style={{ maxHeight: 220, overflowY: "auto", paddingRight: 4 }}>
                                    {(segments || []).map((segment) => {
                                    const checked = campaignForm.segments.includes(String(segment.id));
                                    return (
                                        <Card key={segment.id} variant={checked ? "surface" : "classic"}>
                                            <Flex align="center" gap="3">
                                                <Checkbox checked={checked} onCheckedChange={(value) => toggleSegment(segment.id, Boolean(value))} />
                                                <Flex gap="1" align="center" justify="between" style={{ flex: 1 }}>
                                                    <Text weight="medium">{segment.name}</Text>
                                                    <Badge size="1">
                                                        <PersonIcon width={12} height={12} />
                                                        {(segment.contact_count ?? segment.cached_contact_count ?? 0).toLocaleString()} contacts
                                                    </Badge>
                                                </Flex>
                                            </Flex>
                                        </Card>
                                    );
                                    })}
                                </Flex>
                                )}
                            </Flex>
                          </Card>

                            {/* Tags card */}
                          <Card variant={campaignForm.audience_type === "tag" ? "surface" : "classic"}
                            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                            <Text as="label" size="2" style={{ width: "100%" }}>
                                <Flex align="center" gap="3">
                                    <RadioGroup.Item value="tag" />Tags
                                    <Tooltip content="Selected tag contacts are evaluated; only opted-in contacts with a deliverable, non-suppressed email address will be sent to.">
                                        <InfoCircledIcon />
                                    </Tooltip>
                                </Flex>
                            </Text>

                            <Flex direction="column" gap="3" style={{ flex: 1, display: campaignForm.audience_type === "tag" ? "flex" : "none" }}>
                                {campaignForm.audience_type === "tag" && (
                                  (contactTags || []).length === 0 ? (
                                    <Text size="2" color="gray">No tags available. Create tags from the Audience page first.</Text>
                                  ) : (
                                    <Flex direction="column" gap="2"
                                        style={{ maxHeight: 220, overflowY: "auto", paddingRight: 4 }}>
                                        {(contactTags || []).map((tag) => {
                                        const checked = campaignForm.tags.includes(String(tag.id));
                                        return (
                                            <Card key={tag.id} variant={checked ? "surface" : "classic"}>
                                                <Flex align="center" gap="3">
                                                    <Checkbox checked={checked} onCheckedChange={(value) => toggleTag(tag.id, Boolean(value))} />
                                                    <Flex gap="1" align="center" justify="between" style={{ flex: 1 }}>
                                                        <Flex align="center" gap="2">
                                                            <BookmarkIcon width={12} height={12} />
                                                            <Text weight="medium">{tag.name}</Text>
                                                        </Flex>
                                                        <Badge size="1">
                                                            <PersonIcon width={12} height={12} />
                                                            {(tag.contact_count ?? 0).toLocaleString()} contacts
                                                        </Badge>
                                                    </Flex>
                                                </Flex>
                                            </Card>
                                        );
                                        })}
                                    </Flex>
                                  )
                                )}
                            </Flex>
                          </Card>
                        </Flex>
                      </RadioGroup.Root>
                    </Flex>

                    <Card>
                      <Flex direction="column" gap="3">
                        <Text size="2" weight="medium">Email validation options</Text>
                        <Text as="label" size="2">
                          <Flex gap="2" align="start">
                            <Checkbox
                              checked={campaignForm.include_risky_emails}
                              onCheckedChange={(value) => setCampaignForm((prev) => ({
                                ...prev,
                                include_risky_emails: Boolean(value),
                              }))}
                            />
                            <Flex direction="column" gap="1">
                              <Text weight="medium">Include risky email addresses</Text>
                              <Text size="1" color="gray">
                                These addresses could not be fully verified or have characteristics such as catch-all domains. Including them may increase delivery risk.
                              </Text>
                            </Flex>
                          </Flex>
                        </Text>
                        <Text as="label" size="2">
                          <Flex gap="2" align="start">
                            <Checkbox
                              checked={campaignForm.include_unknown_emails}
                              onCheckedChange={(value) => setCampaignForm((prev) => ({
                                ...prev,
                                include_unknown_emails: Boolean(value),
                              }))}
                            />
                            <Flex direction="column" gap="1">
                              <Text weight="medium">Include inconclusive email addresses</Text>
                              <Text size="1" color="gray">
                                These addresses could not be conclusively verified after validation attempts. Addresses still awaiting another check remain excluded.
                              </Text>
                            </Flex>
                          </Flex>
                        </Text>
                        <Text size="1" color="gray">
                          Invalid, pending, checking, missing, unsubscribed, and suppressed addresses cannot be included.
                        </Text>
                      </Flex>
                    </Card>

                    <Card>
                      <Flex direction="column" gap="3">
                        <Flex justify="between" align="center" gap="3" wrap="wrap">
                          <Flex gap="5" align="end" wrap="wrap">
                            <Flex direction="column" gap="1">
                              <Text size="1" color="gray">Selected</Text>
                              <Text size="6" weight="bold">{selectedAudienceCount.toLocaleString()}</Text>
                            </Flex>
                            <Flex direction="column" gap="1">
                              <Text size="1" color="gray">Eligible to send</Text>
                              <Text size="6" weight="bold" color="green">{estimatedRecipients.toLocaleString()}</Text>
                            </Flex>
                            <Flex direction="column" gap="1">
                              <Text size="1" color="gray">Excluded</Text>
                              <Text size="6" weight="bold" color={excludedAudienceCount ? "red" : "gray"}>
                                {excludedAudienceCount.toLocaleString()}
                              </Text>
                            </Flex>
                          </Flex>
                          <Tooltip content="Selected is the full audience scope. Eligible includes opted-in, non-suppressed contacts whose email validation status is allowed by the options above. Exclusion badges show why contacts cannot be sent to; validation badges show the selected audience's email-status breakdown. Risky and terminal inconclusive addresses are eligible only when enabled. Missing, invalid, pending, checking, retry-pending inconclusive, unsubscribed, and suppressed addresses remain excluded.">
                            <InfoCircledIcon />
                          </Tooltip>
                        </Flex>

                        {excludedAudienceCount > 0 && (
                          <Flex gap="2" wrap="wrap" pt="2" style={{ borderTop: "1px solid var(--gray-a5)" }}>
                            {Object.entries(EMAIL_EXCLUSION_LABELS).map(([key, label]) => {
                              const count = Number(audienceExclusions[key] || 0);
                              return count > 0 ? (
                                <Badge key={key} color="gray" variant="soft">
                                  {label}: {count.toLocaleString()}
                                </Badge>
                              ) : null;
                            })}
                          </Flex>
                        )}
                        <Flex gap="2" wrap="wrap" pt="2" style={{ borderTop: "1px solid var(--gray-a5)" }}>
                          <Badge color="green" variant="soft">Valid: {Number(audienceValidationCounts.valid || 0).toLocaleString()}</Badge>
                          <Badge color="amber" variant="soft">Risky: {Number(audienceValidationCounts.risky || 0).toLocaleString()}</Badge>
                          <Badge color="orange" variant="soft">Terminal unknown: {Number(audienceValidationCounts.terminal_unknown || 0).toLocaleString()}</Badge>
                          <Badge color="blue" variant="soft">Pending/checking: {(Number(audienceValidationCounts.pending || 0) + Number(audienceValidationCounts.processing || 0)).toLocaleString()}</Badge>
                          <Badge color="red" variant="soft">Invalid: {Number(audienceValidationCounts.invalid || 0).toLocaleString()}</Badge>
                        </Flex>
                      </Flex>
                    </Card>
                  </>
                )}

                {/* Template selection step */}
                {currentStep === 3 && (
                  <>
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">Email subtype</Text>
                      <Select.Root
                        value={campaignForm.sub_type}
                        onValueChange={(value) => setCampaignForm((prev) => ({ ...prev, sub_type: value, template: "" }))}>
                        <Select.Trigger style={{ width: "100%" }} />
                        <Select.Content>
                          {SUB_TYPE_OPTIONS.map((option) => (
                            <Select.Item key={option.value} value={option.value}>{option.label}</Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    </Flex>

                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">Template</Text>
                      <Select.Root
                        value={campaignForm.template}
                        onValueChange={(value) => setCampaignForm((prev) => ({ ...prev, template: value }))}>
                        <Select.Trigger placeholder="Choose an email template" style={{ width: "100%" }} />
                        <Select.Content>
                          {filteredTemplates.length > 0 ? (
                            filteredTemplates.map((template) => (
                              <Select.Item key={template.id} value={String(template.id)}>{template.name}</Select.Item>
                            ))
                          ) : (
                            <Select.Item value="no-template" disabled>No templates available for this subtype</Select.Item>
                          )}
                        </Select.Content>
                      </Select.Root>
                    </Flex>
                  </>
                )}

                {/* Send mode step */}
                {currentStep === 4 && (
                  <>
                    <Flex direction="column" gap="2">
                      <Text size="2" weight="medium">When should this campaign be sent?</Text>
                      <RadioGroup.Root
                        value={campaignForm.send_mode}
                        onValueChange={(value) => {
                          if (isEditingCampaign && value !== "scheduled") {
                            return;
                          }
                          setCampaignForm((prev) => ({ ...prev, send_mode: value, scheduled_send_at: value === "scheduled" ? prev.scheduled_send_at : "" }));
                        }}
                      >
                        <Flex direction="column" gap="3">
                          {/* Send now card */}
                          <Card variant={campaignForm.send_mode === "now" ? "surface" : "classic"}>
                            <Text as="label" size="2" style={{ width: "100%" }}>
                              <Flex align="center" gap="3">
                                <RadioGroup.Item value="now" disabled={isEditingCampaign} />
                                Send now
                                <Tooltip content="The campaign will be handed off for delivery as soon as you confirm it.">
                                  <InfoCircledIcon />
                                </Tooltip>
                              </Flex>
                            </Text>
                          </Card>

                          {/* Schedule card */}
                          <Card
                            variant={campaignForm.send_mode === "scheduled" ? "surface" : "classic"}
                            style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
                          >
                            <Text as="label" size="2" style={{ width: "100%" }}>
                              <Flex align="center" gap="3">
                                <RadioGroup.Item value="scheduled" />
                                Schedule
                                <Tooltip content="Choose a future date and time. The campaign will be queued and sent at that moment. Uses your browser's local time zone.">
                                  <InfoCircledIcon />
                                </Tooltip>
                              </Flex>
                            </Text>

                            <Flex direction="column" gap="1" style={{ display: campaignForm.send_mode === "scheduled" ? "flex" : "none" }}>
                              {campaignForm.send_mode === "scheduled" && (
                                <>
                                  <DateTimePicker
                                    value={campaignForm.scheduled_send_at ? new Date(campaignForm.scheduled_send_at) : null}
                                    onChange={(date) => setCampaignForm((prev) => ({ ...prev, scheduled_send_at: date ? date.toISOString() : "" }))}
                                    disablePast
                                  />
                                </>
                              )}
                            </Flex>
                          </Card>
                        </Flex>
                      </RadioGroup.Root>
                    </Flex>

                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">Send from</Text>
                      <Select.Root
                        value={campaignForm.sender_domain}
                        onValueChange={(value) => setCampaignForm((prev) => ({ ...prev, sender_domain: value }))}
                      >
                        <Select.Trigger placeholder="Choose a sender email" style={{ width: "100%" }} />
                        <Select.Content>
                          <Select.Item value="default">Default sender (noreply@b-connect.co.uk)</Select.Item>
                          {activeSenderDomains.map((domain) => (
                            <Select.Item key={domain.id} value={String(domain.id)}>
                              {domain.default_sender_name} &lt;{domain.default_sender_email}&gt;
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                      <Text size="1" color="gray">All emails of this campaign will be sent using this as the sender email.</Text>
                    </Flex>
                  </>
                )}

                {/* Review step */}
                {currentStep === 5 && (
                <>
                <Flex align="center" gap="2" justify="between">
                    <Text weight="medium">Campaign summary</Text>
                    <Button variant="soft" color="amber"
                      onClick={() => setTestEmailDialogOpen(true)} disabled={!campaignForm.template}>
                        <EnvelopeOpenIcon width={14} height={14} />
                        Send test email
                    </Button>
                </Flex>
                <Flex gap="3">
                  <Card style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', flex: '1 0 0' }}>
                    <Text size="2" color="gray">Campaign name</Text>
                    <Text weight="medium">{campaignForm.name}</Text>
                    <Text size="1" color="gray">{campaignForm.campaign_type.toUpperCase()} • {campaignForm.sub_type}</Text>
                  </Card>

                  <Card style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', flex: '1 0 0' }}>
                    <Text size="2" color="gray">Audience</Text>
                    <Text weight="medium">
                      {campaignForm.audience_type === "all_contacts"
                        ? "All eligible contacts"
                        : campaignForm.audience_type === "tag"
                          ? (selectedTagNames.join(", ") || "Tags")
                          : (selectedSegmentNames.join(", ") || "Segments")}
                    </Text>
                    <Text size="1" color="gray">
                      {estimatedRecipients.toLocaleString()} eligible of {selectedAudienceCount.toLocaleString()} selected
                      {excludedAudienceCount > 0 ? ` • ${excludedAudienceCount.toLocaleString()} excluded` : ""}
                    </Text>
                    {excludedAudienceCount > 0 && (
                      <Flex gap="1" wrap="wrap" mt="1">
                        {Object.entries(EMAIL_EXCLUSION_LABELS).map(([key, label]) => {
                          const count = Number(audienceExclusions[key] || 0);
                          return count > 0 ? <Badge key={key} color="gray" variant="soft" size="1">{label}: {count}</Badge> : null;
                        })}
                      </Flex>
                    )}
                    <Text size="1" color="gray">
                      Risky addresses {campaignForm.include_risky_emails ? "included" : "excluded"} · Terminal inconclusive addresses {campaignForm.include_unknown_emails ? "included" : "excluded"}
                    </Text>
                  </Card>
                </Flex>

                <Flex gap="3">
                  <Card style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', flex: '1 0 0' }}>
                    <Text size="2" color="gray">Template</Text>
                    <Text weight="medium">{selectedTemplate?.name || "No template selected"}</Text>
                    <Text size="1" color="gray">{selectedTemplate?.purpose || "No subtype"}</Text>
                  </Card>

                  <Card style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', flex: '1 0 0' }}>
                    <Text size="2" color="gray">Schedule</Text>
                    <Text weight="medium">{campaignForm.send_mode === "scheduled" ? "Scheduled" : "Send immediately"}</Text>
                    <Text size="1" color="gray">{campaignForm.send_mode === "scheduled" ? formatDateTime(campaignForm.scheduled_send_at) : "Queued right after confirmation"}</Text>
                  </Card>
                </Flex>

                <Card style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
                  <Flex direction="column" gap="1" style={{ flex: '1 1 0', minWidth: 0 }}>
                    <Text size="2" color="gray">Send from</Text>
                    <Text weight="medium" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {selectedCampaignSenderDomain?.default_sender_email || "noreply@b-connect.co.uk"}
                    </Text>
                  </Flex>

                  <Flex direction="column" align="end" gap="1" style={{ flex: '1 1 0', minWidth: 0 }}>
                    <Text size="2" color="gray">Sender details</Text>
                    <Text size="1" weight="medium" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: '100%' }}>
                      {selectedCampaignSenderDomain
                        ? `${selectedCampaignSenderDomain.default_sender_name} • ${selectedCampaignSenderDomain.domain}`
                        : "Default sender"}
                    </Text>
                  </Flex>
                </Card>
                </>
                )}
              </Flex>
            </Flex>
            <Separator orientation="vertical" size="4" style={dialogSeparatorStyle} />
            <TemplatePreviewPanel template={selectedTemplate} />
          </Flex>

          <Flex gap="3" mt="5" justify="between" align="center" style={{ flex: '0 0 auto' }}>
            <Button variant="soft" color="gray" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>

            <Flex gap="2">
              {currentStep > 1 && (
                <Button variant="soft" color="gray" 
                    onClick={() => setCurrentStep((step) => Math.max(step - 1, 1))}>
                  Back
                </Button>
              )}

              {currentStep < 5 ? (
                <Button onClick={handleNext} disabled={!stepIsValid}>
                    Next
                </Button>
              ) : (
                <Button
                  onClick={handleConfirm}
                  disabled={createCampaignMutation.isPending || updateCampaignMutation.isPending || estimatedRecipients === 0}
                  loading={createCampaignMutation.isPending || updateCampaignMutation.isPending}
                >
                  {getConfirmButtonLabel()}
                </Button>
              )}
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <AlertDialog.Root open={!!campaignToDelete} onOpenChange={(open) => !open && setCampaignToDelete(null)}>
        <AlertDialog.Content maxWidth="420px">
          <AlertDialog.Title>Delete Campaign</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Are you sure you want to delete{" "}
            <Text weight="bold">{campaignToDelete?.name}</Text>?
            This action cannot be undone.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                variant="solid"
                color="red"
                onClick={handleDeleteCampaign}
                loading={deleteCampaignMutation.isPending}
              >
                Delete
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      <AlertDialog.Root open={!!campaignToCancel} onOpenChange={(open) => !open && setCampaignToCancel(null)}>
        <AlertDialog.Content maxWidth="420px">
          <AlertDialog.Title>Cancel Remaining Sends</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Cancel the remaining sends for{" "}
            <Text weight="bold">{campaignToCancel?.name}</Text>?
            Already sent emails cannot be recalled.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">Keep campaign</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                variant="solid"
                color="red"
                onClick={() => handleCampaignAction(campaignToCancel, "cancel")}
                loading={campaignActionMutation.isPending}
              >
                Cancel remaining
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      <Dialog.Root open={testEmailDialogOpen} onOpenChange={setTestEmailDialogOpen}>
        <Dialog.Content style={{ maxWidth: 450 }}>
          <Dialog.Title>Send Test Email</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Enter the email address where you'd like to receive a test version of this campaign's template.
          </Dialog.Description>

          <Flex direction="column" gap="3">
            <label>
              <Text as="div" size="2" mb="1" weight="medium">
                Email address
              </Text>
              <TextField.Root
                placeholder="example@email.com"
                value={testEmailAddress}
                onChange={(e) => {
                  setTestEmailAddress(e.target.value);
                  setTestEmailError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSendTestEmail();
                  }
                }}
              />
              {testEmailError && (
                <Text as="div" size="1" color="red" mt="1">
                  {testEmailError}
                </Text>
              )}
            </label>

            {/* Sender domain selector */}
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">Send from</Text>
              <Select.Root value={testSenderDomainId} onValueChange={setTestSenderDomainId}>
                <Select.Trigger style={{ width: "100%" }} />
                <Select.Content>
                  <Select.Item value="default">Default (noreply@b-connect.co.uk)</Select.Item>
                  {(senderDomains || []).filter((d) => d.status === "verified" && d.link_status === "verified" && d.is_active).map((d) => (
                    <Select.Item key={d.sender_domain_id} value={d.sender_domain_id}>
                      {d.default_sender_name} &lt;{d.default_sender_email}&gt;
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              <Text size="1" color="gray">Choose which sender domain to use for this test email.</Text>
            </Flex>
          </Flex>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" disabled={isSendingTest}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              onClick={handleSendTestEmail}
              disabled={isSendingTest || !testEmailAddress.trim()}
              loading={isSendingTest}
            >
              Send
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <ToastAlert />
    </>
  );
}
